import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import type {
  BuilderSection,
  WebsiteBuilderPage,
  WebsiteBuilderState,
} from 'types/websiteBuilder';
import {
  BUILDER_STORAGE_KEY,
  createDefaultWebsiteBuilderState,
  createSection,
} from './builderTemplates';
import { ROUTES } from 'utils/constants';
import { syncHeaderNavigationWithPages } from './pageNavSync';
import Canvas from './Canvas';
import styles from './WebsiteBuilder.module.scss';
import heroPlaceholder from 'assets/websiteBuilderDefaults/hero-placeholder.svg';

function normalizeSlugParam(input: string | undefined): string {
  return (input ?? '').trim().replace(/^\/+/, '').toLowerCase();
}

function isWebsiteBuilderState(x: unknown): x is WebsiteBuilderState {
  if (typeof x !== 'object' || x == null) return false;
  const r = x as Record<string, unknown>;
  return Array.isArray(r.sections) && typeof r.pageBackgroundUrl === 'string';
}

function loadFromStorage(
  defaultState: WebsiteBuilderState,
): WebsiteBuilderState {
  try {
    const raw = localStorage.getItem(BUILDER_STORAGE_KEY);
    if (!raw) return defaultState;
    const parsed = JSON.parse(raw) as unknown;

    // Back-compat: some versions stored just sections (array).
    if (Array.isArray(parsed)) {
      return syncHeaderNavigationWithPages({
        ...defaultState,
        sections: parsed as BuilderSection[],
      });
    }

    if (isWebsiteBuilderState(parsed)) {
      return syncHeaderNavigationWithPages({
        sections: parsed.sections,
        pageBackgroundUrl: parsed.pageBackgroundUrl,
        pages: Array.isArray(parsed.pages) ? parsed.pages : [],
      });
    }

    return defaultState;
  } catch {
    return defaultState;
  }
}

function resolveActivePage(
  pages: WebsiteBuilderPage[],
  slugParam: string | undefined,
): WebsiteBuilderPage | null {
  if (pages.length === 0) return null;
  const target = normalizeSlugParam(slugParam);
  const match = pages.find((p) => normalizeSlugParam(p.slug) === target);
  if (match) return match;

  // Special handling: /home should fallback to the first page.
  if (target === 'home') return pages[0];
  return null;
}

function PublicWebsitePage() {
  const { slug } = useParams();
  const defaultState = useMemo(() => createDefaultWebsiteBuilderState(), []);

  const [state, setState] = useState<WebsiteBuilderState>(defaultState);

  const load = useCallback(() => {
    setState(loadFromStorage(defaultState));
  }, [defaultState]);

  useEffect(() => {
    load();
  }, [load, slug]);

  const pages = useMemo(
    () => (Array.isArray(state.pages) ? state.pages : []),
    [state.pages],
  );
  const activePage = useMemo(
    () => resolveActivePage(pages, slug),
    [pages, slug],
  );

  const shared = useMemo(() => {
    const header = (state.sections ?? []).find((s) => s.type === 'header');
    const cta = (state.sections ?? []).find((s) => s.type === 'cta');
    const footer = (state.sections ?? []).find((s) => s.type === 'footer');
    return { header, cta, footer };
  }, [state.sections]);

  const bodySections = useMemo(() => {
    const resolved: WebsiteBuilderPage | null = activePage ?? null;
    const raw = resolved?.bodySections;
    const isDonatePage =
      normalizeSlugParam(resolved?.slug) === 'donate' ||
      resolved?.id === 'page-default-donate';
    const fallback = createSection('body', 0);
    if (isDonatePage) {
      fallback.data = {
        ...fallback.data,
        variant: 'donate',
        title: 'Support Our Ministry',
        subtitle: 'We need your help.',
        body: 'Your giving helps us continue outreach, discipleship, and community care.',
        donateImageUrl: '',
        donateSuggestedAmounts: '100,250,500,1000',
        donateBankOptions: 'GCash,BDO,UnionBank',
        donateMode: 'one-time',
        donateButtonLabel: 'Give Now',
        donateNote: 'Secure online giving. Thank you for your generosity.',
        thankYouTitle: 'Thank you for your gift!',
        thankYouSubtitle: 'Your donation will help local wildlife thrive.',
        thankYouReceiptText:
          "We've sent your donation receipt to [donor email address]",
        thankYouShareFacebookLabel: 'Share on Facebook',
        thankYouShareInstagramLabel: 'Share on Instagram',
      };
    }
    const list = Array.isArray(raw) && raw.length > 0 ? raw : [fallback];

    return list.map((s, i) => ({ ...s, order: i }));
  }, [activePage]);

  const composedSections: BuilderSection[] = useMemo(() => {
    const parts: BuilderSection[] = [];
    let order = 0;

    if (shared.header) parts.push({ ...shared.header, order: order++ });
    for (const s of bodySections) parts.push({ ...s, order: order++ });
    if (shared.cta) parts.push({ ...shared.cta, order: order++ });
    if (shared.footer) parts.push({ ...shared.footer, order });

    return parts;
  }, [bodySections, shared]);

  const hasCustomPageBackground = state.pageBackgroundUrl.trim() !== '';
  const effectivePageBackgroundUrl =
    state.pageBackgroundUrl.trim() !== '' ? state.pageBackgroundUrl : '';

  return (
    <div
      className={styles.container}
      style={{
        // Public website routes intentionally hide the global app header.
        // Use full viewport height to avoid a white strip at the bottom.
        height: '100vh',
        background: 'hsl(220 15% 18%)',
      }}
    >
      <main
        className={styles.canvasArea}
        style={
          hasCustomPageBackground
            ? {
                backgroundImage: `url(${effectivePageBackgroundUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              }
            : {
                backgroundImage: `linear-gradient(hsl(0 0% 35% / 0.55), hsl(0 0% 35% / 0.55)), url(${heroPlaceholder})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundBlendMode: 'saturation',
              }
        }
      >
        {activePage ? (
          <Canvas
            sections={composedSections}
            previewMode
            onSelectSection={(id) => {
              void id;
            }}
            onUpdateSectionData={(id, data) => {
              void id;
              void data;
            }}
          />
        ) : (
          <div
            style={{
              padding: '2rem 1rem',
              color: 'hsl(0 0% 60%)',
              textAlign: 'center',
            }}
          >
            <h2 style={{ margin: 0, fontSize: '1.25rem' }}>Page not found</h2>
            <p style={{ marginTop: '0.75rem' }}>
              No website page was found for <code>{slug}</code>.{' '}
              <span>
                Go back to{' '}
                <a
                  href={`${ROUTES.MEMBERSHIP.WEBSITE}/${pages[0]?.slug ?? 'home'}`}
                >
                  {(pages[0]?.slug ?? 'home').toString()}
                </a>
              </span>
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

export default PublicWebsitePage;
